import { useState, useCallback, useMemo } from "react"
import type { UseStepNavigationReturn, SlideDirection } from "../types"
import { AUDIT_STEPS } from "../constants"

interface UseStepNavigationProps {
  validateStep: (step: number) => boolean
  onClose: () => void
  onReset: () => void
}

export const useStepNavigation = ({
  validateStep,
  onClose,
  onReset
}: UseStepNavigationProps): UseStepNavigationReturn => {
  const [activeStep, setActiveStep] = useState(0)
  const [previousStep, setPreviousStep] = useState(0)
  const [slideDirection, setSlideDirection] = useState<SlideDirection>("left")
  const [isTransitioning, setIsTransitioning] = useState(false)

  const isStepValid = useMemo(() => {
    return validateStep(activeStep)
  }, [activeStep, validateStep])

  const handleNext = useCallback(() => {
    if (activeStep < AUDIT_STEPS.length - 1 && !isTransitioning && isStepValid) {
      setPreviousStep(activeStep)
      setSlideDirection("left")
      setIsTransitioning(true)

      // Change step immediately, transition will handle the animation
      setActiveStep((prev) => prev + 1)

      // Reset transition state after animation completes
      setTimeout(() => setIsTransitioning(false), 300)
    }
  }, [activeStep, isTransitioning, isStepValid])

  const handleBack = useCallback(() => {
    if (activeStep > 0 && !isTransitioning) {
      setPreviousStep(activeStep)
      setSlideDirection("right")
      setIsTransitioning(true)

      // Change step immediately, transition will handle the animation
      setActiveStep((prev) => prev - 1)

      // Reset transition state after animation completes
      setTimeout(() => setIsTransitioning(false), 300)
    }
  }, [activeStep, isTransitioning])

  const handleClose = useCallback(() => {
    setActiveStep(0)
    setPreviousStep(0)
    setSlideDirection("left")
    setIsTransitioning(false)
    onReset()
    onClose()
  }, [onClose, onReset])

  return {
    activeStep,
    previousStep,
    slideDirection,
    isTransitioning,
    isStepValid: isStepValid && !isTransitioning,
    handleNext,
    handleBack,
    handleClose
  }
}
